import React, { useCallback, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import { actionCreators as drawerActionCreators } from "../lenderReportingDrawer.store";
import { actionCreators as dscrDashboardActionCreators } from "../../reportComponentsSetup/dscrSetup/dscrSetup.store";
import { actionCreators as dscrCalculationActionCreators } from "../../reportingComponents/dscrCalculationDashboard/dscrRegularCalculationDashboard/dscrRegularCalculationDashboard.store";

import { actionCreators as dscrLineItemActionCreators } from "./dscrLineItemManagement.store";
import { StyledDoneButton } from "./styles";
import { DSCR_TYPE_NAMES, validateDscrInputs } from "./helpers";

const AddDscrLineItemButton = props => {
  const {
    resultValue,
    setDscrCalculationDashboardSelections,
    setDscrSetupSelections,
    selectedDscrType,
    calculationFieldsInputValues,
    selectedComparisonType1,
    selectedComparisonType2,
    lineItemType,
    activeLoanId,
    dscrCalculations,
    calculatedComparisonValue1,
    calculatedComparisonValue2,
    comparisonCalculationFieldsInputValues1,
    comparisonCalculationFieldsInputValues2,
    setDscrLineItemsToDefault,
    calculateMostRecentPayment,
    calculateRateReplacement,
    calculateProjectedCashflows,
    calculateTrailingCashflows,
    calculateHypotheticalAmortization,
    calculateMaxInterestPlusActualPrincipal,
    recentAsOfDate,
    onDoneCalculate,
    setDscrLineItemSelections,
    selectedFromFileField
  } = props;

  const calculateDscrType = group => {
    let selectedType;
    let calculations;

    if (lineItemType === "value") {
      selectedType = selectedDscrType;
      calculations = calculationFieldsInputValues;
    } else {
      selectedType = group === "first" ? selectedComparisonType1 : selectedComparisonType2;
      calculations = group === "first" ? comparisonCalculationFieldsInputValues1 : comparisonCalculationFieldsInputValues2;
    }

    // eslint-disable-next-line default-case
    switch (selectedType.type) {
      case DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT:
        calculateMostRecentPayment({ loanId: activeLoanId, group, asOfDate: recentAsOfDate });
        break;
      case DSCR_TYPE_NAMES.RATE_REPLACEMENT:
        {
          const cashflowsValues = calculations.find(calc => calc.fieldName === "cashflows");
          const periodsCovered = cashflowsValues.additionalInputValue.type;
          const periods = cashflowsValues.inputValue;

          const replacementRate = parseFloat(calculations.find(calc => calc.fieldName === "rate").inputValue, 10) / 100;

          calculateRateReplacement({ loanId: activeLoanId, asOfDate: recentAsOfDate, periodsCovered, periods, replacementRate, group });
        }
        break;
      case DSCR_TYPE_NAMES.ACTUAL_PROJECTED_CASHFLOWS:
        {
          const periods = calculations.find(calc => calc.fieldName === "Actual projected cashflows").inputValue;
          calculateProjectedCashflows({ loanId: activeLoanId, asOfDate: recentAsOfDate, periods, group });
        }
        break;
      case DSCR_TYPE_NAMES.ACTUAL_TRAILING_CASHFLOWS:
        {
          const periods = calculations.find(calc => calc.fieldName === "Actual trailing cashflows").inputValue;
          calculateTrailingCashflows({ loanId: activeLoanId, asOfDate: recentAsOfDate, periods, group });
        }
        break;
      case DSCR_TYPE_NAMES.HYPOTHETICAL_AMORTIZATION:
        {
          const periods = calculations.find(calc => calc.fieldName === "dscr amortization term").inputValue;
          const fixedRate = parseFloat(calculations.find(calc => calc.fieldName === "dscr fixed rate").inputValue, 10) / 100;
          const treasurySpread = parseFloat(calculations.find(calc => calc.fieldName === "dscr treasury spread").inputValue, 10) / 100;
          const floatingRateSpread = parseFloat(calculations.find(calc => calc.fieldName === "dscr floating rate spread").inputValue, 10) / 100;
          const dscrSwapRate = parseFloat(calculations.find(calc => calc.fieldName === "dscr swap rate").inputValue, 10) / 100;
          const floatingRateCalcMethod = calculations.find(calc => calc.fieldName === "index calculated on")?.additionalInputValue?.type;
          const loanBalanceType = calculations.find(calc => calc.fieldName === "loan balance")?.additionalInputValue?.type;

          calculateHypotheticalAmortization({
            loanId: activeLoanId,
            asOfDate: recentAsOfDate,
            periods,
            fixedRate: fixedRate || null,
            treasurySpread: treasurySpread || null,
            floatingRateSpread: floatingRateSpread || null,
            dscrSwapRate: dscrSwapRate || null,
            group,
            floatingRateMethod: floatingRateCalcMethod || null,
            loanBalanceType: loanBalanceType || null
          });
        }
        break;
        case DSCR_TYPE_NAMES.MAX_INTEREST_PLUS_ACTUAL_PRINCIPAL:
        {
          const floatingRateSpread = parseFloat(calculations.find(calc => calc.fieldName === "dscr floating rate spread").inputValue, 10) / 100;
          const floatingRateCalcMethod = calculations.find(calc => calc.fieldName === "index calculated on")?.additionalInputValue?.type;

          calculateMaxInterestPlusActualPrincipal({
            loanId: activeLoanId,
            asOfDate: recentAsOfDate,
            group,
            floatingRateSpread: floatingRateSpread || null,
            floatingRateMethod: floatingRateCalcMethod || null
          });
        }
        break;
    }
  };

  const onDone = () => {
    // * save after values calculated
    if (resultValue) {
      let newLineItem;
      if (lineItemType === "value") {
        newLineItem = {
          selectedDscrType,
          lineItemType,
          calculationFieldsInputValues,
          type: selectedDscrType.type,
          value: resultValue
        };
        if(selectedDscrType.type === DSCR_TYPE_NAMES.FROM_FILE) {
          newLineItem.selectedFromFileField = selectedFromFileField;
          newLineItem.calculationFieldsInputValues = [selectedFromFileField];
        }
      } else {
        newLineItem = {
          lineItemType,
          selectedComparisonType1,
          selectedComparisonType2,
          value: resultValue,
          type: calculatedComparisonValue1 >= calculatedComparisonValue2 ? selectedComparisonType1.type : selectedComparisonType2.type,
          calculatedComparisonValue1,
          calculatedComparisonValue2,
          comparisonCalculationFieldsInputValues1,
          comparisonCalculationFieldsInputValues2
        };
        if(selectedComparisonType1.type === DSCR_TYPE_NAMES.FROM_FILE || selectedComparisonType2.type === DSCR_TYPE_NAMES.FROM_FILE) {
          if(selectedComparisonType1.type === DSCR_TYPE_NAMES.FROM_FILE) {
          newLineItem.comparisonCalculationFieldsInputValues1 = [selectedFromFileField];
          } else {
            newLineItem.comparisonCalculationFieldsInputValues2 = [selectedFromFileField];
          }
          newLineItem.selectedFromFileField = selectedFromFileField;
        }
      }
      const updatedCalculations =
        dscrCalculations &&
        dscrCalculations.map(calc => {
          if (calc.id !== activeLoanId) {
            return calc;
          }

          return { ...calc, lineItem: newLineItem };
        });
      setDscrSetupSelections({ dscrCalculations: updatedCalculations });
      setDscrCalculationDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
      setDscrLineItemsToDefault();
    } else {
      // * calculate and save
      if (lineItemType === "value") {
        calculateDscrType();
      } else {
        calculateDscrType("first");
        calculateDscrType("second");
      }
      setDscrLineItemSelections({ onDoneCalculate: true });
    }
  };

  useEffect(() => {
    if (onDoneCalculate && resultValue) {
      setDscrLineItemSelections({ onDoneCalculate: false });
      onDone();
    }
  }, [onDoneCalculate, resultValue]);

  const isReadyToCalculate = useCallback(() => {
    if (lineItemType === "value") {
      if (selectedDscrType && (selectedDscrType.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT || selectedDscrType.type === DSCR_TYPE_NAMES.FROM_FILE)) {
        return true;
      }

      return calculationFieldsInputValues && validateDscrInputs(calculationFieldsInputValues, selectedDscrType.type);
    }
    const isFirstGroupReady =
      selectedComparisonType1 &&
      ((selectedComparisonType1.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT || selectedComparisonType1.type === DSCR_TYPE_NAMES.FROM_FILE) ||
        dscrInputsCondition(comparisonCalculationFieldsInputValues1, selectedComparisonType1));
    const isSecondGroupReady =
      selectedComparisonType2 &&
      ((selectedComparisonType2.type === DSCR_TYPE_NAMES.MOST_RECENT_PAYMENT || selectedComparisonType2.type === DSCR_TYPE_NAMES.FROM_FILE) ||
        dscrInputsCondition(comparisonCalculationFieldsInputValues2, selectedComparisonType2));

    return isFirstGroupReady && isSecondGroupReady;
  }, [
    lineItemType,
    selectedDscrType,
    calculationFieldsInputValues,
    comparisonCalculationFieldsInputValues1,
    comparisonCalculationFieldsInputValues2,
    selectedComparisonType1,
    selectedComparisonType2
  ]);

  return <StyledDoneButton disabled={!isReadyToCalculate()} label="Done" onClick={onDone} />;
};

const dscrInputsCondition = (comparisonCalculationFieldInput,selectedComparisonType) => comparisonCalculationFieldInput
                                                                                       && validateDscrInputs(comparisonCalculationFieldInput, selectedComparisonType.type);
const mapStateToProps = ({ lenderReportDscrLineItemManagement, lenderReportsDscrSetup, lenderReportsDscrCalculation }) => ({
  ...lenderReportDscrLineItemManagement,
  ...lenderReportsDscrSetup,
  ...lenderReportsDscrCalculation
});

const mapDispatchToProps = dispatch => bindActionCreators({ ...dscrLineItemActionCreators, ...drawerActionCreators, ...dscrDashboardActionCreators, ...dscrCalculationActionCreators }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AddDscrLineItemButton);